"use client"

import type { ReactNode } from "react"
import { motion, useInView } from "framer-motion"
import { useEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"

/**
 * Motion primitives used by the section library.
 * Some sections import these from `@/components/motion-primitives`.
 * This file is the canonical location for those primitives.
 */

export function AmbientGlow({
  className,
  duration = 6,
}: {
  className?: string
  duration?: number
}) {
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0.6, scale: 0.95 }}
      animate={{ opacity: [0.6, 0.9, 0.6], scale: [0.95, 1.05, 0.95] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
      className={cn("pointer-events-none absolute rounded-full blur-3xl", className)}
    />
  )
}

export function LiftCard({
  children,
  lift = 4,
  scale = 1.01,
  className,
}: {
  children: ReactNode
  lift?: number
  scale?: number
  className?: string
}) {
  return (
    <motion.div
      whileHover={{ y: -lift, scale, transition: { duration: 0.3, ease: "easeOut" } }}
      className={cn("h-full", className)}
    >
      {children}
    </motion.div>
  )
}

export function TiltCard({
  children,
  intensity = 6,
  className,
}: {
  children: ReactNode
  intensity?: number
  className?: string
}) {
  return (
    <motion.div
      whileHover={{ rotateX: intensity * 0.5, rotateY: -intensity, transition: { duration: 0.4, ease: "easeOut" } }}
      style={{ transformPerspective: 800 }}
      className={cn("h-full", className)}
    >
      {children}
    </motion.div>
  )
}

export function MagneticCta({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <motion.span
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      className={cn("inline-flex", className)}
    >
      {children}
    </motion.span>
  )
}

export function CountUpNumber({
  target,
  prefix = "",
  suffix = "",
  duration = 1.6,
  className,
}: {
  target: number
  prefix?: string
  suffix?: string
  duration?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" })
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let frame = 0
    const start = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / (duration * 1000))
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))
      if (progress < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [isInView, target, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}
      {value.toLocaleString()}
      {suffix}
    </span>
  )
}
